import type { PerformancePart } from '@tunasong/schemas'
import { performancePartLabels, performanceParts, stageInputStandLabels } from '@tunasong/schemas'
import { Button, Checkbox, FormControlLabel, Radio, RadioGroup, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { presetTiers } from './presets.js'
import type { PresetRow } from './presets.js'
import type { NewStageInput } from './setup-edits.js'
import styles from './preset-step.module.css'

export interface PresetStepProps {
  /** The parts the member plays; one tier group per part that has presets, in canonical order */
  parts: readonly PerformancePart[]
  /** Moved focus here when the "Add inputs" prompt opened the step */
  autoFocus?: boolean
  onAdd(rows: NewStageInput[]): void
}

const rowLabel = (row: PresetRow) =>
  [
    row.source,
    row.micDi,
    row.stand && row.stand !== 'none' ? stageInputStandLabels[row.stand] : undefined,
    row.phantomPower ? '48V' : undefined,
  ]
    .filter(Boolean)
    .join(' · ')

/** Row keys the member unticked, as `${part}:${index}` */
type Unticked = Set<string>

/**
 * "Start from a preset": per part, a radio group of tiers with nothing pre-selected. Picking a tier shows its rows as
 * a pre-ticked checklist; Add selected adds the ticked rows and forgets the tier.
 */
export const PresetStep: FC<PresetStepProps> = props => {
  const { parts, autoFocus, onAdd } = props
  const headingId = useId()
  const [tiers, setTiers] = useState<Partial<Record<PerformancePart, string>>>({})
  const [unticked, setUnticked] = useState<Unticked>(new Set())
  const headingRef = useRef<HTMLElement>(null)

  /** Opened from the "Add inputs" prompt: move focus to the step once, on mount */
  useEffect(() => {
    if (autoFocus) {
      headingRef.current?.focus()
    }
  }, [autoFocus])

  const withPresets = performanceParts.filter(part => parts.includes(part) && presetTiers[part])

  const chosenRows = withPresets.flatMap(part => {
    const tier = presetTiers[part]?.find(t => t.id === tiers[part])
    return (tier?.rows ?? [])
      .map((row, index) => ({ part, row, key: `${part}:${index}` }))
      .filter(({ key }) => !unticked.has(key))
  })

  const handleTier = (part: PerformancePart, tierId: string) => {
    setTiers(current => ({ ...current, [part]: tierId }))
    // A new tier starts fully ticked
    setUnticked(current => new Set([...current].filter(key => !key.startsWith(`${part}:`))))
  }

  const handleTick = (key: string) => (checked: boolean) =>
    setUnticked(current => {
      const next = new Set(current)
      if (checked) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })

  const handleAdd = () => {
    onAdd(chosenRows.map(({ part, row }) => ({ ...row, part, providedByArtist: false })))
    setTiers({})
    setUnticked(new Set())
  }

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Typography id={headingId} variant="h6" component="h3" className={styles.title} tabIndex={-1} ref={headingRef}>
        Start from a preset
      </Typography>
      {withPresets.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          Pick what you play above to see presets.
        </Typography>
      ) : (
        <>
          {withPresets.map(part => {
            const tier = presetTiers[part]?.find(t => t.id === tiers[part])
            const label = performancePartLabels[part]
            return (
              <div key={part} className={styles.part}>
                <Typography variant="subtitle2" component="h4" className={styles.partTitle}>
                  {label}
                </Typography>
                <RadioGroup
                  aria-label={`${label} preset`}
                  value={tiers[part] ?? ''}
                  onChange={(_event, value) => handleTier(part, value)}
                >
                  {(presetTiers[part] ?? []).map(option => (
                    <FormControlLabel
                      key={option.id}
                      className={styles.choice}
                      control={<Radio value={option.id} />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {tier ? (
                  <fieldset className={styles.checklist}>
                    <legend className={styles.legend}>Add from {label} preset</legend>
                    {tier.rows.map((row, index) => {
                      const key = `${part}:${index}`
                      return (
                        <FormControlLabel
                          key={key}
                          className={styles.choice}
                          control={<Checkbox checked={!unticked.has(key)} onCheckedChange={handleTick(key)} />}
                          label={rowLabel(row)}
                        />
                      )
                    })}
                  </fieldset>
                ) : null}
              </div>
            )
          })}
          <div>
            <Button variant="contained" disabled={chosenRows.length === 0} onClick={handleAdd}>
              Add selected
            </Button>
          </div>
        </>
      )}
    </section>
  )
}

export default PresetStep
